import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import api from '../utils/api';
import BrandMark from '../components/BrandMark';
import './Auth.css';

export default function Signup() {
  const { signup, loginWithGoogle } = useAuth();
  const { lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const tokenClientRef = useRef(null);
  const loginWithGoogleRef = useRef(loginWithGoogle);
  loginWithGoogleRef.current = loginWithGoogle;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const langRef = useRef(lang);
  langRef.current = lang;

  useEffect(() => {
    let cancelled = false;

    const googleEnabled = (cfg) =>
      cfg &&
      String(cfg.google_client_id || '').trim() &&
      (cfg.google_login_active === 1 || cfg.google_login_active === true);

    const initTokenClient = (clientId) => {
      if (cancelled || !window.google?.accounts?.oauth2) return;
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'email profile',
        callback: async (response) => {
          if (response.error) {
            setError(
              response.error_description ||
                response.error ||
                (langRef.current === 'en' ? 'Google login failed' : 'Error al iniciar con Google'),
            );
            return;
          }
          if (response.access_token) {
            const result = await loginWithGoogleRef.current(response.access_token);
            if (result.success) navigateRef.current('/app');
            else setError(result.msg);
          }
        },
      });
      setGoogleReady(true);
    };

    api
      .get('/web/get_web_public')
      .then((r) => {
        const cfg = r.data?.data;
        if (!googleEnabled(cfg)) return;
        const clientId = String(cfg.google_client_id).trim();

        if (window.google?.accounts?.oauth2) {
          initTokenClient(clientId);
          return;
        }

        const src = 'https://accounts.google.com/gsi/client';
        let script = document.querySelector(`script[src="${src}"]`);
        if (!script) {
          script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        }
        script.addEventListener('load', () => initTokenClient(clientId), { once: true });
        if (window.google?.accounts?.oauth2) initTokenClient(clientId);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const handleGoogleLogin = () => {
    tokenClientRef.current?.requestAccessToken();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError(lang === 'en' ? 'Passwords do not match' : 'Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError(lang === 'en' ? 'Password must be at least 6 characters' : 'La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!phone.trim()) {
      setError(lang === 'en' ? 'Phone number is required' : 'El número de teléfono es obligatorio');
      return;
    }

    setLoading(true);
    const res = await signup({ name, email, password, mobile_with_country_code: phone });
    setLoading(false);

    if (res.success) {
      navigate('/app');
    } else {
      setError(res.msg);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-particles">
        {[...Array(12)].map((_, i) => (
          <span key={i} className="auth-particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 8}s`,
            animationDuration: `${6 + Math.random() * 10}s`,
          }} />
        ))}
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo" onClick={() => navigate('/')}>
            <BrandMark
              imgClassName="auth-logo-icon auth-logo-icon--mark"
              fallbackClassName="auth-logo-icon"
            />
            <span>ReplyTek</span>
          </div>
          <button type="button" className="auth-lang" onClick={toggleLang}>{lang === 'en' ? 'ES' : 'EN'}</button>
        </div>

        <h1>{lang === 'en' ? 'Create your account' : 'Crea tu cuenta'}</h1>
        <p className="auth-subtitle">
          {lang === 'en' ? 'Start automating WhatsApp in minutes' : 'Empieza a automatizar WhatsApp en minutos'}
        </p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="signup-name">{lang === 'en' ? 'Full name' : 'Nombre completo'}</label>
            <input
              id="signup-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={lang === 'en' ? 'John Doe' : 'Juan Pérez'}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="signup-email">{lang === 'en' ? 'Email' : 'Correo electrónico'}</label>
            <input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={lang === 'en' ? 'your@email.com' : 'tu@correo.com'}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="signup-phone">{lang === 'en' ? 'Phone (with country code)' : 'Teléfono (con código de país)'}</label>
            <input
              id="signup-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 305 555 1234"
              required
            />
          </div>

          <div className="auth-row">
            <div className="auth-field">
              <label htmlFor="signup-password">{lang === 'en' ? 'Password' : 'Contraseña'}</label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="auth-field">
              <label htmlFor="signup-confirm">{lang === 'en' ? 'Confirm' : 'Confirmar'}</label>
              <input
                id="signup-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div className="auth-terms">
            <span>{lang === 'en' ? 'By creating an account, you agree to our ' : 'Al crear una cuenta, aceptas nuestros '}</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">{lang === 'en' ? 'Terms' : 'Términos'}</a>
            <span> & </span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">{lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}</a>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading
              ? (lang === 'en' ? 'Creating account...' : 'Creando cuenta...')
              : (lang === 'en' ? 'Create Account' : 'Crear Cuenta')}
          </button>
        </form>

        {googleReady && (
          <>
            <div className="auth-divider">
              <span>{lang === 'en' ? 'or' : 'o'}</span>
            </div>
            <button type="button" className="auth-social" onClick={handleGoogleLogin}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {lang === 'en' ? 'Continue with Google' : 'Continuar con Google'}
            </button>
          </>
        )}

        <p className="auth-switch">
          {lang === 'en' ? 'Already have an account? ' : '¿Ya tienes cuenta? '}
          <Link to="/login">{lang === 'en' ? 'Sign in' : 'Inicia sesión'}</Link>
        </p>
      </div>
    </div>
  );
}
